/**
 * Kantera Design System — Awwwards-grade shared tokens & primitives.
 *
 * Every page imports from here so the visual language stays cohesive.
 * NO Tailwind — CSSProperties only.
 *
 * Now backed by CSS custom properties for theme switching.
 * The actual color values live in `apps/web/src/index.css`.
 */
// ---------------------------------------------------------------------------
// Color tokens — CSS variable backed
// ---------------------------------------------------------------------------
export const C = {
    bg: {
        deep: 'var(--k-bg-deep)',
        base: 'var(--k-bg-base)',
        card: 'var(--k-bg-card)',
        elevated: 'var(--k-bg-elevated)',
        hover: 'var(--k-bg-hover)',
        input: 'var(--k-bg-input)',
    },
    accent: {
        indigo: 'var(--k-accent)',
        indigoGlow: 'var(--k-accent-glow)',
        indigoSoft: 'rgba(var(--k-accent-rgb), 0.08)',
        violet: '#a78bfa', // decorative — not theme-switched
        emerald: 'var(--k-brand)',
    },
    success: 'var(--k-success)',
    danger: 'var(--k-danger)',
    warning: 'var(--k-warning)',
    info: 'var(--k-info)',
    text: {
        primary: 'var(--k-text-primary)',
        secondary: 'var(--k-text-secondary)',
        muted: 'var(--k-text-muted)',
        faint: 'var(--k-text-faint)',
        bright: '#ffffff',
    },
    border: 'var(--k-border)',
    borderHover: 'var(--k-border-hover)',
    glass: 'var(--k-glass)',
    glassStrong: 'var(--k-glass-strong)',
    radius: {
        xs: '6px',
        sm: '8px',
        md: '14px',
        lg: '20px',
        xl: '28px',
        full: '9999px',
    },
    shadow: {
        sm: '0 2px 8px rgba(0,0,0,0.3)',
        md: '0 8px 24px rgba(0,0,0,0.4)',
        lg: '0 12px 48px rgba(0,0,0,0.5)',
        glow: '0 0 20px rgba(var(--k-accent-rgb), 0.15)',
        glowStrong: '0 0 32px rgba(var(--k-accent-rgb), 0.25)',
    },
};
// ---------------------------------------------------------------------------
// CSS-variable-safe alpha helper
// ---------------------------------------------------------------------------
// The C.* tokens are CSS variable strings (e.g., 'var(--k-accent)').
// Appending hex opacity like `${C.accent.indigo}30` produces invalid CSS.
// Use alpha() instead: alpha(C.accent.indigo, 0.19)
const TOKEN_TO_RGB = {
    'var(--k-accent)': 'var(--k-accent-rgb)',
    'var(--k-brand)': 'var(--k-brand-rgb)',
    'var(--k-success)': 'var(--k-success-rgb)',
    'var(--k-danger)': 'var(--k-danger-rgb)',
    'var(--k-warning)': 'var(--k-warning-rgb)',
    'var(--k-info)': 'var(--k-info-rgb)',
    'var(--k-text-primary)': 'var(--k-text-primary-rgb)',
    'var(--k-bg-base)': 'var(--k-bg-base-rgb)',
};
/**
 * Returns a CSS-variable-safe rgba() string for any C.* token.
 * For hex colors (like C.accent.violet = '#a78bfa'), returns standard rgba().
 * For CSS variable tokens, maps to the corresponding -rgb variable.
 *
 * @example
 *   alpha(C.accent.indigo, 0.19) → 'rgba(var(--k-accent-rgb), 0.19)'
 *   alpha(C.success, 0.12) → 'rgba(var(--k-success-rgb), 0.12)'
 *   alpha('#a78bfa', 0.3) → 'rgba(167, 139, 250, 0.3)'
 */
export function alpha(token, opacity) {
    // If it's a hex color, parse it directly
    if (token.startsWith('#')) {
        const hex = token.slice(1);
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // Map CSS variable token to its RGB counterpart
    const rgbVar = TOKEN_TO_RGB[token];
    if (rgbVar) {
        return `rgba(${rgbVar}, ${opacity})`;
    }
    // Fallback: try to extract the variable name and append -rgb
    const match = token.match(/var\(([^)]+)\)/);
    if (match) {
        return `rgba(var(${match[1]}-rgb), ${opacity})`;
    }
    // Last resort: return the token as-is (won't break, just won't have alpha)
    return token;
}
// Common hex-to-decimal opacity conversions:
// 08 = 0.03, 0d = 0.05, 10 = 0.06, 12 = 0.07, 15 = 0.08, 18 = 0.09
// 20 = 0.12, 22 = 0.13, 25 = 0.15, 30 = 0.19, 33 = 0.20, 40 = 0.25
// 44 = 0.27, 50 = 0.31, 60 = 0.38, 70 = 0.44, 80 = 0.50
// ---------------------------------------------------------------------------
// Keyframe injection — call once per page
// ---------------------------------------------------------------------------
const KF_ID = 'kantera-design-v3';
export function injectKeyframes() {
    if (typeof document === 'undefined' || document.getElementById(KF_ID))
        return;
    const s = document.createElement('style');
    s.id = KF_ID;
    s.textContent = `
    @keyframes kReveal { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform: translateY(0); } }
    @keyframes kFadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes kPulseGlow { 0%, 100% { opacity:0.4; } 50% { opacity:0.7; } }
    @keyframes kFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
    @keyframes kShimmer { 0% { background-position: -600px 0; } 100% { background-position: 600px 0; } }
    @keyframes kCountUp { from { opacity:0; transform: scale(0.8); } to { opacity:1; transform: scale(1); } }
    @keyframes kSlideLeft { from { opacity:0; transform: translateX(20px); } to { opacity:1; transform: translateX(0); } }
    @keyframes kSlideUp { from { opacity:0; transform: translateY(12px); } to { opacity:1; transform: translateY(0); } }
    @keyframes kRingDraw { from { stroke-dashoffset: 283; } }
    @keyframes kBorderGlow { 0%, 100% { border-color: rgba(var(--k-accent-rgb), 0.15); } 50% { border-color: rgba(var(--k-accent-rgb), 0.35); } }
    @keyframes kSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;
    document.head.appendChild(s);
}
// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------
/** Page wrapper — full bleed dark background */
export const pageWrap = {
    background: C.bg.base,
    minHeight: '100vh',
    padding: '0 24px 48px',
    color: C.text.primary,
};
/** Cinematic page hero — gradient header band */
export function heroGradient(_accentColor = C.accent.indigo) {
    return {
        position: 'relative',
        marginTop: 0,
        marginRight: '-24px',
        marginLeft: '-24px',
        padding: '40px 32px 32px',
        background: `linear-gradient(135deg, ${C.bg.deep} 0%, ${C.bg.card} 50%, rgba(var(--k-accent-rgb), 0.03) 100%)`,
        borderBottom: `1px solid ${C.border}`,
        overflow: 'hidden',
        marginBottom: '24px',
    };
}
/** Glass panel header */
export const glassHeader = {
    padding: '14px 20px',
    background: C.glassStrong,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};
/** Section title */
export const sectionTitle = {
    fontSize: '13px',
    fontWeight: 700,
    color: C.text.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
};
/** Stat number — large tabular */
export const statNumber = {
    fontSize: '28px',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
    lineHeight: 1,
};
/** Stat label — small muted uppercase */
export const statLabel = {
    fontSize: '11px',
    fontWeight: 600,
    color: C.text.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
};
/** Card surface */
export function cardSurface(hovered = false) {
    return {
        background: hovered ? C.bg.hover : C.bg.elevated,
        border: `1px solid ${hovered ? C.borderHover : C.border}`,
        borderRadius: C.radius.md,
        transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered ? C.shadow.lg : C.shadow.sm,
    };
}
/** Glass panel surface */
export function glassSurface() {
    return {
        background: C.glass,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: `1px solid ${C.border}`,
        borderRadius: C.radius.lg,
        overflow: 'hidden',
    };
}
/** Input field styling */
export const inputStyle = {
    padding: '10px 14px',
    background: C.bg.input,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius.sm,
    color: C.text.primary,
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 150ms ease',
};
/** Pill button base */
export function pillButton(active = false, accent = C.accent.indigo) {
    return {
        padding: '7px 16px',
        borderRadius: C.radius.full,
        border: `1.5px solid ${active ? accent : C.border}`,
        background: active ? 'rgba(var(--k-accent-rgb), 0.09)' : 'transparent',
        color: active ? accent : C.text.secondary,
        fontSize: '13px',
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        transition: 'all 150ms ease',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
    };
}
/** Animated orb — decorative background element */
export function orbStyle(size, _color, top, right, delay = 0) {
    return {
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: `radial-gradient(circle, rgba(var(--k-accent-rgb), 0.12) 0%, transparent 70%)`,
        top,
        right,
        animation: `kPulseGlow 4s ease-in-out infinite`,
        animationDelay: `${delay}ms`,
        pointerEvents: 'none',
    };
}
/** Shimmer skeleton block */
export function shimmer(w = '100%', h = 16, delay = 0) {
    return {
        width: w,
        height: typeof h === 'number' ? `${h}px` : h,
        borderRadius: C.radius.sm,
        // See styles.ts shimmerBlock — the shorthand would reset the backgroundSize below.
        backgroundImage: `linear-gradient(90deg, ${C.bg.elevated} 0px, #1a1a28 200px, ${C.bg.elevated} 400px)`,
        backgroundSize: '600px 100%',
        animation: 'kShimmer 1.5s ease-in-out infinite',
        animationDelay: `${delay}ms`,
    };
}
/** Hash a string to a hue — deterministic color from text */
export function hashToHue(text) {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % 360;
}
//# sourceMappingURL=designTokens.js.map